/**
 * Push Notification Hook
 * Manages browser push notification subscription and permissions
 */

import { useState, useEffect, useCallback } from 'react';
import { API } from '@/App';

// VAPID public key for web push (this is a placeholder - would need real key in production)
const VAPID_PUBLIC_KEY = 'BEl62iUYgUivxIkv69yViEuiBIa-Ib9-SkvMeAtA3LFgDzkrxZJjSgSnfckjBJuBkr3qBUYIHBQFLXYp5Nksh8U';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function usePushNotifications() {
  const [permission, setPermission] = useState('default');
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Check if push notifications are supported
  useEffect(() => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    setIsSupported(supported);
    
    if (supported) {
      setPermission(Notification.permission);
      checkExistingSubscription();
    }
  }, []);

  // Check for existing subscription
  const checkExistingSubscription = async () => {
    try {
      const registration = await navigator.serviceWorker.getRegistration('/sw-push.js');
      if (registration) {
        const sub = await registration.pushManager.getSubscription();
        if (sub) {
          setSubscription(sub);
          setIsSubscribed(true);
        }
      }
    } catch (err) {
      console.error('Failed to check subscription:', err);
    }
  };

  // Register service worker
  const registerServiceWorker = async () => {
    try {
      const registration = await navigator.serviceWorker.register('/sw-push.js', {
        scope: '/'
      });
      console.log('Service Worker registered:', registration);
      return registration;
    } catch (err) {
      console.error('Service Worker registration failed:', err);
      throw err;
    }
  };

  // Request permission and subscribe
  const subscribe = useCallback(async () => {
    if (!isSupported) {
      setError('Push notifications are not supported in this browser');
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      // Request permission
      const perm = await Notification.requestPermission();
      setPermission(perm);
      
      if (perm !== 'granted') {
        setError('Notification permission denied');
        setLoading(false);
        return false;
      }

      // Register service worker
      let registration = await navigator.serviceWorker.getRegistration('/sw-push.js');
      if (!registration) {
        registration = await registerServiceWorker();
      }

      // Wait for service worker to be ready
      await navigator.serviceWorker.ready;

      // Subscribe to push
      const sub = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
      });

      // Send subscription to server
      const response = await fetch(`${API}/auth/push-subscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          subscription: sub.toJSON()
        })
      });

      if (response.ok) {
        setSubscription(sub);
        setIsSubscribed(true);
        setLoading(false);
        return true;
      } else {
        throw new Error('Failed to save subscription on server');
      }
    } catch (err) {
      console.error('Push subscription failed:', err);
      setError(err.message || 'Failed to enable push notifications');
      setLoading(false);
      return false;
    }
  }, [isSupported]);

  // Unsubscribe
  const unsubscribe = useCallback(async () => {
    if (!subscription) return false;

    setLoading(true);
    setError(null);

    try {
      await subscription.unsubscribe();
      
      // Notify server
      await fetch(`${API}/auth/push-subscription`, {
        method: 'DELETE',
        credentials: 'include'
      });

      setSubscription(null);
      setIsSubscribed(false);
      setLoading(false);
      return true;
    } catch (err) {
      console.error('Push unsubscribe failed:', err);
      setError(err.message || 'Failed to disable push notifications');
      setLoading(false);
      return false;
    }
  }, [subscription]);

  // Test notification (for debugging)
  const testNotification = useCallback(() => {
    if (permission !== 'granted') {
      setError('Permission not granted');
      return;
    }

    new Notification('Test Notification', {
      body: 'Push notifications are working!',
      icon: '/eduzy-logo-sm.png',
      tag: 'test'
    });
  }, [permission]);

  return {
    isSupported,
    permission,
    isSubscribed,
    subscription,
    loading,
    error,
    subscribe,
    unsubscribe,
    testNotification
  };
}

export default usePushNotifications;
